import React, { useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Loader2, Phone, MessageSquare, Mail, CalendarClock, RefreshCcw, Bot } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const CHANNEL_ICON = {
  chiama: Phone,
  whatsapp: MessageSquare,
  email: Mail,
  incontro: CalendarClock,
};

const PRIO = {
  ALTA:  { bg: 'rgba(239,68,68,0.15)',  color: '#FCA5A5', border: 'rgba(239,68,68,0.25)' },
  MEDIA: { bg: 'rgba(245,158,11,0.15)', color: '#FCD34D', border: 'rgba(245,158,11,0.25)' },
  BASSA: { bg: 'rgba(255,255,255,0.08)', color: '#94A3B8', border: 'rgba(255,255,255,0.12)' },
};

export default function CoachCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post("/coach/today");
      setData(r.data);
      if (r.data.warning) toast.warning("Risposta AI parziale");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore Coach");
    } finally { setLoading(false); }
  };

  return (
    <div className="text-white" style={{ background: "linear-gradient(145deg, #0A192F 0%, #0E2040 60%, #172A45 100%)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 4, boxShadow: "0 1px 3px rgba(10,25,47,0.12)" }} data-testid="coach-card">
      <div className="px-5 py-3.5 border-b border-white/8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-[#B7955B]" />
          <h3 className="font-serif text-base text-white">Coach AI — focus di oggi</h3>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/coach" className="text-xs text-[#B7955B] hover:underline" data-testid="coach-card-pb-link">Playbook completo →</Link>
          <button onClick={run} disabled={loading} data-testid="coach-run" className="text-xs flex items-center gap-1.5 px-3 py-1.5 border border-white/10 hover:bg-white/8 rounded-[3px] transition-colors">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
            {data ? "Rigenera" : "Avvia"}
          </button>
        </div>
      </div>
      <div className="p-5">
        {!data && !loading && (
          <div className="text-sm text-slate-300 flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-[#B7955B] mt-0.5" />
            Avvia il Coach: l'AI analizza il portafoglio e ti suggerisce le 5 azioni più importanti da fare oggi.
          </div>
        )}
        {loading && (
          <div className="text-sm text-slate-300 flex items-center gap-2 py-4">
            <Loader2 className="w-4 h-4 animate-spin text-[#B7955B]" /> Analisi in corso...
          </div>
        )}
        {data?.empty && (
          <div className="text-sm text-slate-300 italic py-2">{data.message}</div>
        )}
        {data && !data.empty && (
          <ul className="space-y-3">
            {(data.suggestions || []).map((sg, i) => {
              const Icon = CHANNEL_ICON[(sg.channel || "").toLowerCase()] || Phone;
              return (
                <li key={i} className="flex items-start gap-3 pb-3 border-b border-white/8/60 last:border-0 last:pb-0" data-testid={`coach-sg-${i}`}>
                  <div className="w-8 h-8 rounded-sm bg-[#172A45] border border-[#23375a] flex items-center justify-center text-[#B7955B] shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${PRIO[sg.priority] || PRIO.BASSA}`}>{sg.priority || "—"}</span>
                      <span className="text-sm text-white font-medium truncate">
                        {sg.clientId
                          ? <Link to={`/clienti/${sg.clientId}`} className="hover:text-[#B7955B]">{sg.clientName || "Cliente"}</Link>
                          : (sg.clientName || "Cliente")}
                      </span>
                    </div>
                    <div className="text-xs text-slate-300 leading-relaxed">{sg.reason}</div>
                    <div className="text-xs text-[#B7955B] mt-1 font-medium">→ {sg.suggestedAction}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {data?.stats && (
          <div className="mt-3 pt-3 border-t border-[#23375a]/60 flex items-center justify-between text-[11px] text-slate-400 tabular-nums">
            <span>Hot {data.stats.hot} • Scaduti {data.stats.expired} • Recall oggi {data.stats.todayRecall}</span>
            <span>Appt 7gg {data.stats.upcomingAppts} • Pratiche urgenti {data.stats.urgentPractices}</span>
          </div>
        )}
      </div>
    </div>
  );
}
